/**
 * Which of an investor's dealflow rows belong to a SEALED company (119-07).
 *
 * A sealed company's profile is unreadable to every client (119-02), so its
 * dealflow row arrives with a NULL `startup` embed (`profileMissing`). A NULL
 * embed is a hint, not proof — so the answer comes from `company_public_card`,
 * which returns a card ONLY for a sealed company:
 *
 *   not profileMissing         → operating (render the ordinary card)
 *   card returned              → sealed (render ClosedCompanyCard)
 *   card query loading / error → loading / error (ClosedCompanyCard's branches)
 *   no card                    → operating: not sealed, just not readable;
 *                                keep today's card rather than guess
 *
 * One query for the whole list (`useCompanyPublicCards`), shared by key with
 * startup-detail.
 */

import { useCallback, useMemo } from 'react';
import { useCompanyPublicCards, type CompanyPublicCard } from '@/lib/company/public-card';
import type { DealflowStartup } from '@/lib/state/dealflow-store';

export type SealedDealState =
  | { kind: 'operating' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'sealed'; card: CompanyPublicCard };

type DealLike = Pick<DealflowStartup, 'profileMissing'> & { startup: { id: string } };

/** Pure: the state of one deal given the card query's result. */
export function sealedDealState(
  deal: DealLike,
  cards: ReadonlyMap<string, CompanyPublicCard> | undefined,
  isLoading: boolean,
  isError: boolean,
): SealedDealState {
  if (!deal.profileMissing) return { kind: 'operating' };
  const card = cards?.get(deal.startup.id.toLowerCase());
  if (card) return { kind: 'sealed', card };
  if (isLoading) return { kind: 'loading' };
  if (isError) return { kind: 'error' };
  return { kind: 'operating' };
}

export function useSealedDeals(deals: readonly DealLike[]) {
  const ids = useMemo(
    () => deals.filter((d) => d.profileMissing).map((d) => d.startup.id),
    [deals],
  );
  const q = useCompanyPublicCards(ids);
  const stateFor = useCallback(
    (deal: DealLike) => sealedDealState(deal, q.data, q.isLoading, q.isError),
    [q.data, q.isLoading, q.isError],
  );
  const sealedStartupIds = useMemo(
    () => new Set(q.data ? [...q.data.keys()] : []),
    [q.data],
  );
  return { stateFor, sealedStartupIds, retry: () => void q.refetch() };
}

/**
 * startup-detail: which id to ask the card RPC about, or null.
 *   - a pipeline deal whose profile is missing → its startup id;
 *   - nothing resolved locally (a cold link from the closed-companies list, a
 *     thread or a notification) → the route id, which may be a sealed
 *     company's startup id; any other id space simply returns no card.
 * A resolved, readable startup is never asked about: it is not sealed.
 */
export function sealedDetailCandidate(args: {
  deal: DealLike | null | undefined;
  hasStartup: boolean;
  routeId: string | null | undefined;
}): string | null {
  if (args.deal?.profileMissing) return args.deal.startup.id;
  if (!args.hasStartup && args.routeId) return args.routeId;
  return null;
}
